import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./AccessoriesHero.css";
import heroImg from "../../../../assets/images/accessories/acchero.png";
import { resolveMediaUrl } from "../../../../utils/media";
import { getPageContent } from "../../../../api/content";

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const unwrapSectionData = (section) => {
  if (section == null) return section;
  if (typeof section === "string") {
    const t = section.trim();
    if (t.startsWith("{")) {
      try {
        const parsed = JSON.parse(t);
        if (parsed && typeof parsed === "object") return unwrapSectionData(parsed);
      } catch {
        return section;
      }
    }
    return section;
  }
  if (typeof section !== "object") return section;
  if (section.data && typeof section.data === "object") return section.data;
  return section;
};

/** Mirrors the field aliases used by OtherProducts so the same CMS image is shown here. */
const getItemImageFromOtherProducts = (content, itemNumber) => {
  if (!content || typeof content !== "object") return "";
  const suffix = String(itemNumber);
  const candidates = [
    content[`item${suffix}Image`],
    content[`item${suffix}image`],
    content[`item${suffix}Img`],
    content[`item${suffix}img`],
    content[`item${suffix}Icon`],
    content[`item${suffix}icon`],
    content[`item${suffix}Media`],
    content[`item${suffix}media`],
    content[`item${suffix}MediaUrl`],
    content[`item${suffix}mediaUrl`],
    content[`item${suffix}Photo`],
    content[`item${suffix}photo`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const DEFAULT_DESCRIPTION =
  "IRQGYP is a leading Iraqi company specialized in gypsum products and modern construction solutions - serving local and regional markets with a strong commitment to quality, sustainability, and long-term partnerships.";

const DESCRIPTION_BY_TITLE = {
  drywallmetalframe:
    "Our drywall metal framing systems provide strength, precision, and durability. creating a reliable structure for walls, ceilings, and partitions. Easy to install and fully system-compatible, they ensure stability, accuracy, and long-lasting performance.",
  ceilingmetalframe:
    "Our ceiling metal framing systems provide strength, stability, and precision for suspended ceiling applications, creating a dependable structure for flat and decorative ceiling designs",
  ceilingmetal:
    "Our ceiling metal framing systems provide strength, stability, and precision for suspended ceiling applications, creating a dependable structure for flat and decorative ceiling designs",
  accessories:
    "Our accessories are designed to complete drywall and ceiling systems with precision and reliability, enhancing installation efficiency and overall performance. Fully compatible with our boards and metal framing systems, they ensure secure finishing.",
};

const getDescriptionForTitle = (title) => {
  const key = String(title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  return DESCRIPTION_BY_TITLE[key] || DEFAULT_DESCRIPTION;
};

const AccessoriesHero = ({ title = "Accessories", imageSlot, image }) => {
  const { i18n } = useTranslation();
  const [apiImage, setApiImage] = useState("");
  const description = getDescriptionForTitle(title);

  useEffect(() => {
    if (image) return undefined;
    if (!imageSlot) return undefined;

    let isMounted = true;
    const load = async () => {
      try {
        const data = await getPageContent({
          page: "home",
          language: (i18n.language || "en").toLowerCase(),
        });
        const section = unwrapSectionData(findSection(data, "otherproducts"));
        const resolved = getItemImageFromOtherProducts(section, imageSlot);
        if (isMounted) setApiImage(resolved || "");
      } catch {
        if (isMounted) setApiImage("");
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [image, imageSlot, i18n.language]);

  const rightImage = image || apiImage || heroImg;

  return (
    <section className="accessoriesHero">
      <div className="accessoriesHero-container">
        <div className="accessoriesHero-left">
          <span className="accessoriesHero-tag">CATEGORIES</span>

          <h1 className="accessoriesHero-title">{title}</h1>

          <p className="accessoriesHero-desc">{description}</p>

          <div className="accessoriesHero-link">
            Explore all products
            <span className="arrow" aria-hidden="true">
              ↓
            </span>
          </div>
        </div>

        <div className="accessoriesHero-right">
          <img
            src={rightImage}
            alt={title}
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src !== heroImg) {
                e.currentTarget.src = heroImg;
              }
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default AccessoriesHero;
